import { ErrorBanner, Skeleton } from '@/components';
import { useRegisterCompany } from '@/features/company-admin';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchUserLevels } from '../api';
import type { RegisterCompanyError } from '../api/companyApi';
import type { CompanyFormData, ContactFormData, Level } from '../models';
import { CompanyAddressSection } from './CompanyAddressSection';
import { CompanyContactSection } from './CompanyContactSection';
import { CompanyDataSection } from './CompanyDataSection';
import { FormSection } from '@/components/FormSection';
import { useCountries } from '@/features/country/hooks/useCountries';

interface CreateCompanyProps {
    onCreatedSuccessfully: (company: { uuid: string, name: string }) => void;
    withCompanyContact: boolean;
    parentCompany?: string;
    showLevelSelect?: boolean;
    currentCompanyId?: string;
    showCustomerIdField?: boolean;
}

const emptyCompanyFormData: CompanyFormData = {
    companyName: '',
    street: '',
    houseNumber: '',
    addressLine2: '',
    postalCode: '',
    city: '',
    country: '',
    phoneNumber: '',
    email: '',
    taxNumber: '',
    uidNumber: '',
    customerId: '',
};

const emptyContactFormData: ContactFormData = {
    salutation: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    contactEmail: '',
    contactPhone: '',
};

export function CreateCompany({
    onCreatedSuccessfully,
    withCompanyContact,
    parentCompany,
    showLevelSelect = false,
    currentCompanyId,
    showCustomerIdField = false
}: CreateCompanyProps) {
    const { t } = useTranslation();
    const [companyFormData, setCompanyFormData] = useState<CompanyFormData>(emptyCompanyFormData);
    const [contactFormData, setContactFormData] = useState<ContactFormData>(emptyContactFormData);
    const [selectedLevelId, setSelectedLevelId] = useState<string | null>(null);
    const [availableLevels, setAvailableLevels] = useState<Level[]>([]);
    const [isLoadingLevels, setIsLoadingLevels] = useState(false);

    const { data: allCountries, isLoading: countriesLoading } = useCountries();
    const [error, setError] = useState<string | null>(null);

    const registerMutation = useRegisterCompany();

    useEffect(() => {
        if (showLevelSelect && currentCompanyId) {
            setIsLoadingLevels(true);
            fetchUserLevels(currentCompanyId)
                .then((response) => {
                    setAvailableLevels(response || []);
                })
                .catch((err) => {
                    console.error('Failed to fetch user levels:', err);
                })
                .finally(() => {
                    setIsLoadingLevels(false);
                });
        }
    }, [showLevelSelect, currentCompanyId]);

    const handleCompanyChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setCompanyFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleContactChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setContactFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(companyFormData, withCompanyContact ? contactFormData : null);
    };

    const isFormValid = (withCompanyContact: boolean) => {
        const requiredCompanyFields = [
            companyFormData.companyName,
            companyFormData.country,
            companyFormData.uidNumber,
            companyFormData.street,
            companyFormData.postalCode,
            companyFormData.city,
        ];

        const requiredContactFields = withCompanyContact ? [
            contactFormData.salutation,
            contactFormData.firstName,
            contactFormData.lastName,
        ] : [];

        return [...requiredCompanyFields, ...requiredContactFields].every(
            (field) => field.trim() !== ''
        );
    };

    const onSave = (companyData: CompanyFormData, contactData: ContactFormData | null) => {
        setError(null);

        registerMutation.mutate(
            {
                country_id: companyData.country,
                company_identifier: companyData.uidNumber,
                tax_number: companyData.taxNumber,
                company_name: companyData.companyName,
                email: companyData.email,
                phone_number: companyData.phoneNumber,
                company_contact: contactData ? {
                    salutation: contactData.salutation === 'mr' ? 1 : contactData.salutation === 'mrs' ? 2 : 3,
                    first_name: contactData.firstName,
                    last_name: contactData.lastName,
                    date_of_birth: contactData.dateOfBirth,
                    email: contactData.contactEmail,
                    phone_number: contactData.contactPhone,
                } : null,
                address: {
                    street: companyData.street,
                    postal_code: companyData.postalCode,
                    city: companyData.city,
                    country: companyData.country
                },
                parentCompany: parentCompany,
                levelId: selectedLevelId ?? undefined,
                customerId: showCustomerIdField ? companyData.customerId : undefined
            },
            {
                onSuccess: (resp) => {
                    if (resp.success && resp.company_id) {
                        onCreatedSuccessfully({
                            uuid: resp.company_id,
                            name: companyData.companyName
                        });
                    } else {
                        setError(t('companyAdmin.form.saveError'));
                    }
                },
                onError: (err) => {
                    const error = err as RegisterCompanyError | null;
                    if (error !== null && error.type === 'DUPLICATE_TAX_NUMBER') {
                        setError(t('companyAdmin.errors.duplicateTaxNumber'));
                    } else {
                        setError(t('companyAdmin.form.saveError'));
                    }
                }
            }
        );
    };

    if (countriesLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Skeleton className="h-8 w-32" />
            </div>
        );
    }

    const countries = allCountries?.countries ?? [];


    return (

        <form onSubmit={handleSubmit} className="flex flex-col bg-surface w-full">

            <div className="flex flex-col gap-10">
                <CompanyDataSection
                    formData={companyFormData}
                    countries={countries}
                    onChange={handleCompanyChange}
                    showCustomerId={showCustomerIdField}
                />

                <CompanyAddressSection
                    formData={companyFormData}
                    onChange={handleCompanyChange}
                />

                {withCompanyContact &&
                    <CompanyContactSection
                        formData={contactFormData}
                        onChange={handleContactChange}
                    />
                }

                {showLevelSelect && (
                    <FormSection icon="settings" title={t('companyAdmin.form.adminSection')}>
                        <div className="flex flex-col gap-2">
                            <label className="block text-sm font-medium text-onsurface">
                                {t('companyAdmin.form.level')}
                            </label>
                            {isLoadingLevels ? (
                                <Skeleton className="h-10 w-full" />
                            ) : (
                                <select
                                    value={selectedLevelId ?? ''}
                                    onChange={(e) => setSelectedLevelId(e.target.value || null)}
                                    className="py-2 px-3 block w-full border border-border rounded-lg text-sm text-onsurface bg-surface focus:border-primary focus:ring-primary"
                                >
                                    <option value="">{t('companyAdmin.form.selectLevel')}</option>
                                    {availableLevels.map((level) => (
                                        <option key={level.id} value={level.id}>
                                            {level.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </FormSection>
                )}

                <ErrorBanner hasError={!!error}>
                    {error}
                </ErrorBanner>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={registerMutation.isPending || !isFormValid(withCompanyContact)}
                        className="py-2 px-4 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-transparent bg-primary text-onprimary hover:bg-primary-darker disabled:opacity-50 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {registerMutation.isPending ? (
                            <>
                                <svg
                                    className="animate-spin size-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                {t('companyAdmin.form.saving')}
                            </>
                        ) : (
                            t('companyAdmin.form.save')
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
}
